import {
  FiscalArchiveDownloadResponse,
  FiscalArchiveFilters,
  FiscalArchiveObject,
  GtPeriodicFilters,
  GtPeriodicObject,
  JetEventObject,
  JetEventsFilters,
  Paginated,
  PaginatedResponse,
  Sortable,
  SourceDocumentFilters,
  SourceDocumentObject,
} from "@ac-expo/api-types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { nf525QueryKeys } from "../query-keys/nf525.query-keys";

const getJetEvents = async (
  filters: Sortable<Paginated<JetEventsFilters>>
) => {
  try {
    const { data } = await privateAxios.get<{
      events: JetEventObject[];
      total: number;
    }>("/nf525/jet/events", { params: filters });
    return { data: data.events, count: data.total };
  } catch (error) {
    if (isAxiosError(error)) {
      throw { message: error.response?.data.message };
    }
    throw error;
  }
};

export const useGetJetEvents = (
  filters: Sortable<Paginated<JetEventsFilters>>
) => {
  return useQuery({
    queryKey: nf525QueryKeys.jetEvents(filters),
    queryFn: () => getJetEvents(filters),
  });
};

const getSourceDocuments = async (
  filters: Sortable<Paginated<SourceDocumentFilters>>
) => {
  try {
    const { data } = await privateAxios.get<
      PaginatedResponse<SourceDocumentObject>
    >("/nf525/documents", { params: filters });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { message: error.response?.data.message };
    }
    throw error;
  }
};

export const useGetSourceDocuments = (
  filters: Sortable<Paginated<SourceDocumentFilters>>
) => {
  return useQuery({
    queryKey: nf525QueryKeys.sourceDocuments(filters),
    queryFn: () => getSourceDocuments(filters),
  });
};

const getGtPeriodic = async (
  filters: Sortable<Paginated<GtPeriodicFilters>>
) => {
  try {
    const { data } = await privateAxios.get<
      PaginatedResponse<GtPeriodicObject>
    >("/nf525/gt-periodic", { params: filters });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { message: error.response?.data.message };
    }
    throw error;
  }
};

export const useGetGtPeriodic = (
  filters: Sortable<Paginated<GtPeriodicFilters>>
) => {
  return useQuery({
    queryKey: nf525QueryKeys.gtPeriodic(filters),
    queryFn: () => getGtPeriodic(filters),
  });
};

const getFiscalArchives = async (
  tenantId: string,
  filters?: FiscalArchiveFilters
) => {
  try {
    const { data } = await privateAxios.get<FiscalArchiveObject[]>(
      `/nf525/fiscal-archives/by-tenant/${tenantId}`,
      { params: filters }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { message: error.response?.data.message };
    }
    throw error;
  }
};

export const useGetFiscalArchives = (
  tenantId: string,
  filters?: FiscalArchiveFilters
) => {
  return useQuery({
    queryKey: nf525QueryKeys.fiscalArchives(tenantId, filters),
    queryFn: () => getFiscalArchives(tenantId, filters),
    enabled: !!tenantId,
  });
};

const getFiscalArchiveDownloadUrl = async (archiveId: string) => {
  try {
    const { data } = await privateAxios.get<FiscalArchiveDownloadResponse>(
      `/nf525/fiscal-archives/${archiveId}/download`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { message: error.response?.data.message };
    }
    throw error;
  }
};

export const useDownloadFiscalArchive = () => {
  return useMutation({
    mutationFn: (archiveId: string) => getFiscalArchiveDownloadUrl(archiveId),
  });
};

export type MissingClosuresResponse = {
  missingDays: string[];
};

const getMissingClosures = async () => {
  try {
    const { data } = await privateAxios.get<MissingClosuresResponse>(
      "/nf525/closures/daily/missing"
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { message: error.response?.data.message };
    }
    throw error;
  }
};

export const useGetMissingClosures = () => {
  return useQuery({
    queryKey: nf525QueryKeys.missingClosures(),
    queryFn: getMissingClosures,
  });
};

const runDailyClosure = async (dateString: string) => {
  try {
    const { data } = await privateAxios.post("/nf525/closures/daily/run", {
      dateString,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw { message: error.response?.data.message };
    }
    throw error;
  }
};

export const useRunDailyClosure = () => {
  return useMutation({
    mutationFn: (dateString: string) => runDailyClosure(dateString),
  });
};
